import { formatDate } from '@helpers/date';

/*
 * Pure helpers for the Cloud & AI costs page: month arithmetic, money, and the labels that turn
 * the BFF's raw values into words. Kept out of the components so the four provider tabs share one
 * reading of each value, and so it can be tested without rendering anything.
 */

export interface LineItem {
  name: string;
  cost: number;
}

/** Shared shape returned by /aws-costs and /atlas-costs. Amounts are cents. */
export interface ProviderUsage {
  total_usage: number;
  currency?: string;
  daily_costs?: Array<{ date: string; line_items: LineItem[] }>;
  service_breakdown?: LineItem[];
  monthly_limit_cents?: number;
  utilization_pct?: number | null;
  alert?: boolean;
  /** AWS, current month only: Cost Explorer's month-end forecast. */
  forecast?: { state: 'ok'; cents: number } | { state: 'access_denied' | 'unavailable' | 'error'; cents: null; message: string } | null;
  /** Atlas: spend by project and by cluster, from the invoice's line items. */
  project_breakdown?: LineItem[];
  cluster_breakdown?: Array<{ project: string; cluster: string; cost: number }>;
  project_names_resolved?: boolean;
}

export type ModelCategory = 'embedding' | 'llm';

export interface OpenAIUsage {
  total_usage: number;
  daily_costs?: Array<{ timestamp: number; line_items: LineItem[] }>;
  model_breakdown?: Array<LineItem & { category: ModelCategory }>;
  embedding_total?: number;
  llm_total?: number;
  monthly_limit_cents?: number;
  utilization_pct?: number | null;
  alert?: boolean;
}

/** One row of a daily breakdown, whichever provider it came from. */
export interface DailyRow {
  id: string;
  date: string;
  cost: number;
  items: LineItem[];
}

export function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** The current month as YYYY-MM. */
export function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** A YYYY-MM month as an inclusive from/to range, capped at today for the month in progress. */
export function monthRange(month: string, todayStr: string = today()): { from: string; to: string } {
  const [y, m] = month.split('-').map(Number);
  const lastDay = new Date(y, m, 0).getDate(); // day 0 of next month is the last of this one
  const to = `${month}-${String(lastDay).padStart(2, '0')}`;
  return { from: `${month}-01`, to: to > todayStr ? todayStr : to };
}

export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
}

/** Cents as dollars. A missing or non-numeric amount reads "-", never "$NaN". */
export function usd(cents: number | null | undefined): string {
  if (typeof cents !== 'number' || !Number.isFinite(cents)) return '-';
  return `$${(cents / 100).toFixed(2)}`;
}

/** A whole-dollar limit, for "of $50". */
export function usdWhole(cents: number | null | undefined): string {
  if (typeof cents !== 'number' || !Number.isFinite(cents)) return '-';
  return `$${Math.round(cents / 100).toLocaleString('en-US')}`;
}

/** A bucket's date in the console's one date format. */
export function dayLabel(isoDate: string): string {
  return formatDate(isoDate, isoDate);
}

/**
 * The BFF answers 503 with a sentence when a provider's credentials are missing or refused. That
 * is a setup state, not a failure, and the page shows it as "not connected" rather than as an
 * error. The OpenAI and Atlas routes say "not configured", AWS says "not available", and Atlas
 * says "not found" for a wrong org id; all three mean the same thing to an admin.
 */
export const isConfigError = (msg: string) => /not (configured|available|found)/i.test(msg);

const CATEGORY_LABELS: Record<string, string> = { llm: 'LLM', embedding: 'Embeddings' };

/** "llm" -> "LLM", "embedding" -> "Embeddings". Anything else is shown as sent. */
export function categoryLabel(category: string): string {
  return CATEGORY_LABELS[category] ?? category;
}

/**
 * The OpenAI costs API leaves `line_item` empty for spend it cannot tie to a model (project-level
 * charges, mostly), and the BFF fills the gap with "Unknown". That read as a broken lookup; it is
 * a real, correctly totalled amount that simply has no model attached.
 */
export function lineItemLabel(name: string | null | undefined): string {
  const trimmed = (name ?? '').trim();
  if (!trimmed || /^unknown$/i.test(trimmed)) return 'Unattributed';
  return trimmed;
}

/** Sorted, non-zero line items, labelled. */
function spentItems(items: LineItem[]): LineItem[] {
  return items
    .filter((li) => li.cost > 0)
    .map((li) => ({ name: lineItemLabel(li.name), cost: li.cost }))
    .sort((a, b) => b.cost - a.cost);
}

/** Days with spend, newest first. */
export function providerDailyRows(daily: ProviderUsage['daily_costs']): DailyRow[] {
  return (daily ?? [])
    .map((day) => ({
      id: day.date,
      date: day.date,
      cost: day.line_items.reduce((s, li) => s + li.cost, 0),
      items: spentItems(day.line_items),
    }))
    .filter((r) => r.cost > 0)
    .sort((a, b) => (a.date < b.date ? 1 : -1));
}

/** OpenAI buckets are keyed by a unix start time rather than a date. */
export function openAiDailyRows(daily: OpenAIUsage['daily_costs']): DailyRow[] {
  return providerDailyRows(
    (daily ?? []).map((d) => ({
      date: new Date(d.timestamp * 1000).toISOString().slice(0, 10),
      line_items: d.line_items,
    })),
  );
}

export function breakdownRows(items: LineItem[] | undefined): LineItem[] {
  return spentItems(items ?? []);
}

export type BudgetTone = 'success' | 'warning' | 'danger';

/** The one place colour enters the page: how close spend is to the monthly limit. */
export function budgetTone(utilizationPct: number | null | undefined): BudgetTone {
  const pct = typeof utilizationPct === 'number' ? utilizationPct : 0;
  if (pct >= 80) return 'danger';
  if (pct >= 60) return 'warning';
  return 'success';
}

// ---------------------------------------------------------------------------
// Provider states (the BFF answers "cannot read this provider" as data, HTTP 200)
// ---------------------------------------------------------------------------

export type ProviderState = 'ok' | 'not_configured' | 'access_denied' | 'error';

export interface ProviderProblem {
  state: Exclude<ProviderState, 'ok'>;
  message: string;
  missing?: string[];
  steps?: string[];
}

export const isProblem = (v: unknown): v is ProviderProblem =>
  !!v && typeof v === 'object' && typeof (v as any).state === 'string' && (v as any).state !== 'ok';

const STATE_LABELS: Record<ProviderState, string> = {
  ok: 'Connected',
  not_configured: 'Not connected',
  access_denied: 'Access not granted',
  error: 'Could not load',
};

export const stateLabel = (s: ProviderState): string => STATE_LABELS[s] ?? 'Could not load';

// ---------------------------------------------------------------------------
// All costs (/api/cost-summary)
// ---------------------------------------------------------------------------

export type ProviderId = 'aws' | 'openai' | 'mongodb' | 'google' | 'fixed';

export interface ProviderSummary {
  id: ProviderId;
  name: string;
  state: ProviderState;
  message?: string;
  missing?: string[];
  monthToDateCents: number | null;
  lastMonthCents: number | null;
  forecastCents: number | null;
  forecastMethod: 'provider' | 'run_rate' | 'fixed' | null;
  limitCents: number | null;
  note?: string;
}

export interface CostSummary {
  month: string;
  lastMonth: string;
  providers: ProviderSummary[];
  totals: {
    monthToDateCents: number;
    lastMonthCents: number;
    forecastCents: number;
    connected: number;
    of: number;
    complete: boolean;
  };
  inr: { rate: number; isDefault: boolean; monthToDate: number; lastMonth: number; forecast: number };
  fixedItems: Array<{ name: string; usd: number }>;
  generatedAt: string;
}

/** Whole rupees, Indian grouping: "₹4,116". */
export function inr(rupees: number | null | undefined): string {
  if (typeof rupees !== 'number' || !Number.isFinite(rupees)) return '-';
  return `₹${Math.round(rupees).toLocaleString('en-IN')}`;
}

/** Forecast as a share of the monthly limit, or null when there is no limit to measure against. */
export function forecastPct(forecastCents: number | null, limitCents: number | null): number | null {
  if (forecastCents == null || !limitCents) return null;
  return Math.round((forecastCents / limitCents) * 100);
}

const METHOD_LABELS: Record<string, string> = {
  provider: 'AWS forecast',
  run_rate: 'At the current daily rate',
  fixed: 'Fixed',
};

export const forecastMethodLabel = (m: ProviderSummary['forecastMethod']) => (m ? METHOD_LABELS[m] : '-');

// ---------------------------------------------------------------------------
// AI usage ledger (aiUsageSummary)
// ---------------------------------------------------------------------------

export interface AiFeatureRow {
  feature: string;
  service: string;
  model: string | null;
  calls: number;
  tokensIn: number;
  tokensOut: number;
  costUsd: number;
}

export interface AiUsageSummary {
  from: string;
  to: string;
  totalUsd: number;
  byFeature: AiFeatureRow[];
  byDay: Array<{ date: string; costUsd: number }>;
  unpriced: number;
}

/**
 * Ledger feature keys as words. The ledger writes snake_case keys chosen at each call site; the
 * page reads them as the jobs they do. An embedding purpose is plural ("Embeddings: jobs").
 */
const FEATURE_LABELS: Record<string, string> = {
  resume_parsing: 'Resume parsing',
  job_extraction: 'Job extraction',
  job_chat: 'Job chat',
  guide_extraction: 'Guide extraction',
  guide_generation: 'Guide generation',
  guide_revision: 'Guide revision',
  company_draft: 'Company draft',
  seo_description: 'SEO description',
  jobseeker_overview: 'Job seeker overview',
  embeddings_job: 'Embeddings: jobs',
  embeddings_jobseeker: 'Embeddings: job seekers',
  embeddings_api: 'Embeddings: API',
};

const WORDS: Record<string, string> = { jobseeker: 'job seeker', jobseekers: 'job seekers', cv: 'CV', ai: 'AI', seo: 'SEO', api: 'API', llm: 'LLM' };

function sentence(raw: string): string {
  const words = raw
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => WORDS[w.toLowerCase()] ?? w.toLowerCase());
  const s = words.join(' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function featureLabel(feature: string | null | undefined): string {
  const key = (feature ?? '').trim();
  if (!key) return 'Unlabelled';
  if (FEATURE_LABELS[key]) return FEATURE_LABELS[key];
  const emb = /^embeddings?_(.+)$/i.exec(key);
  if (emb) {
    const s = sentence(emb[1]);
    // Lower-case the first letter unless the word is an acronym ("API", "CV").
    const what = /^[A-Z]{2,}\b/.test(s) ? s : s.charAt(0).toLowerCase() + s.slice(1);
    if (/^[A-Z]{2,}$/.test(what) || /s$/.test(what)) return `Embeddings: ${what}`;
    return `Embeddings: ${/[^aeiou]y$/.test(what) ? `${what.slice(0, -1)}ies` : `${what}s`}`;
  }
  return sentence(key);
}

const SERVICE_LABELS: Record<string, string> = {
  backend: 'Backend',
  external_jobs: 'External jobs',
  agentic: 'Recommendations',
};

export const serviceLabel = (service: string | null | undefined): string =>
  SERVICE_LABELS[(service ?? '').trim()] ?? (service ? sentence(service) : '-');

/** Dollars (not cents) with enough precision for a per-feature cost: "$0.0042", "$12.40". */
export function usdPrecise(dollars: number | null | undefined): string {
  if (typeof dollars !== 'number' || !Number.isFinite(dollars)) return '-';
  if (dollars === 0) return '$0.00';
  if (Math.abs(dollars) < 0.01) return `$${dollars.toFixed(4)}`;
  return `$${dollars.toFixed(2)}`;
}

export const count = (n: number | null | undefined) =>
  typeof n === 'number' && Number.isFinite(n) ? n.toLocaleString('en-US') : '-';

/**
 * What OpenAI billed that the ledger did not record, in cents. Positive is spend outside the
 * ledger (the website's own calls, the agentic service before it records, keys used elsewhere);
 * negative means the ledger is ahead of OpenAI's billing, which lags by up to a day.
 */
export function notAttributedCents(billedCents: number | null | undefined, ledgerUsd: number | null | undefined): number | null {
  if (typeof billedCents !== 'number' || typeof ledgerUsd !== 'number') return null;
  return Math.round(billedCents - ledgerUsd * 100);
}
